import { Check, AlertTriangle, XCircle, Building2, MapPin, X, Search } from 'lucide-react';
import './MallWizard.css';

const MallWizard = ({
  cities,
  states,
  malls,
  onSelect,
  onCreateNew,
  selectedState,
  selectedCity,
  selectedArea,
  selectedPincode,
  selectedMallUuid,
  selectedMall,
  stateIsOther,
  cityIsOther,
  areaIsOther,
  setState,
  setCity,
  setAreaIsOther,
  onStateInput,
  onCityInput,
  onPincodeInput,
  onAreaInput,
  pincodeValidation,
  isPincodeValid,
  areaOptions,
  locationReady,
  showMallForm,
  setShowMallForm,
  mallForm,
  onMallFormChange,
  onMallRegister,
  onMallClear,
  onContinue,
  isLoadingMalls,
  mallSearch,
  onMallSearch
}) => {
  const filteredMalls = malls.filter((mall) => {
    if (!mallSearch.trim()) {
      return true;
    }

    const query = mallSearch.toLowerCase();
    return (
      (mall.mallName || '').toLowerCase().includes(query)
      || (mall.city || '').toLowerCase().includes(query)
      || (mall.pincode || '').includes(query)
    );
  });

  const zeroNameMatches = mallSearch.trim() && malls.filter((mall) => (mall.mallName || '').toLowerCase().includes(mallSearch.toLowerCase())).length === 0;

  return (
    <div className="step-block mall-wizard-shell">
      <div className="step-header">
        <h4>Location & Mall</h4>
        <span className="step-pill">Step 1</span>
      </div>

      <div className="form-grid">
        <div className="input-group">
          <label className="input-label">State</label>
          <select
            className="input select-input"
            value={stateIsOther ? '__other__' : (selectedState || '')}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="" disabled>Select State</option>
            {states.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
            <option value="__other__">Other</option>
          </select>
          {stateIsOther && (
            <div className="input-group">
              <label className="input-label">Other State</label>
              <input
                className="input input-other"
                value={selectedState}
                maxLength={50}
                placeholder="e.g., Tamil Nadu"
                onChange={(event) => onStateInput(event.target.value)}
                autoFocus
              />
            </div>
          )}
        </div>

        {(selectedState || stateIsOther) && (
          <div className="input-group">
            <label className="input-label">City</label>
            {!stateIsOther && (
              <select
                className="input select-input"
                value={cityIsOther ? '__other__' : (selectedCity || '')}
                onChange={(event) => setCity(event.target.value)}
              >
                <option value="" disabled>Select City</option>
                {cities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
                <option value="__other__">Other</option>
              </select>
            )}
            {(cityIsOther || stateIsOther) && (
              <div className="input-group">
                <label className="input-label">Other City</label>
                <input
                  className="input input-other"
                  value={selectedCity}
                  maxLength={50}
                  placeholder="e.g., Hyderabad"
                  onChange={(event) => onCityInput(event.target.value)}
                  autoFocus={stateIsOther}
                />
              </div>
            )}
          </div>
        )}

        {(selectedState || stateIsOther) && (
          <div className="input-group">
            <label className="input-label">Pincode</label>
            <input
              className={`input${pincodeValidation && typeof pincodeValidation === 'object' ? (pincodeValidation.status === 'match' ? ' input-valid' : ' input-warn') : ''}`}
              value={selectedPincode}
              maxLength={6}
              placeholder="e.g., 500001"
              onChange={(event) => onPincodeInput(event.target.value.replace(/\D/g, ''))}
            />
            {pincodeValidation === 'loading' && (
              <div className="pincode-badge pincode-badge--loading">
                <span className="pincode-spinner" /> Verifying pincode...
              </div>
            )}
            {pincodeValidation && typeof pincodeValidation === 'object' && pincodeValidation.status === 'match' && (
              <div className="pincode-badge pincode-badge--match">
                <Check size={14} /> Pincode matches {pincodeValidation.apiDistrict && `· ${pincodeValidation.apiDistrict}`}
              </div>
            )}
            {pincodeValidation && typeof pincodeValidation === 'object' && pincodeValidation.status === 'mismatch' && (
              <div className="pincode-badge pincode-badge--mismatch">
                <AlertTriangle size={14} /> Pincode belongs to {[pincodeValidation.apiDistrict, pincodeValidation.apiState].filter(Boolean).join(', ')} — please double check
              </div>
            )}
            {pincodeValidation && typeof pincodeValidation === 'object' && pincodeValidation.status === 'invalid' && (
              <div className="pincode-badge pincode-badge--invalid">
                <XCircle size={14} /> Invalid Pincode
              </div>
            )}
          </div>
        )}

        {isPincodeValid && areaOptions.length > 0 && (
          <div className="input-group">
            <label className="input-label">Area / Locality</label>
            <select
              className="input select-input"
              value={areaIsOther ? '__other__' : (selectedArea || '')}
              onChange={(event) => {
                if (event.target.value === '__other__') {
                  setAreaIsOther(true);
                  onAreaInput('');
                } else {
                  setAreaIsOther(false);
                  onAreaInput(event.target.value);
                }
              }}
            >
              <option value="" disabled>Select Area / Locality</option>
              {areaOptions.map((area) => (
                <option key={area} value={area}>{area}</option>
              ))}
              <option value="__other__">Other</option>
            </select>
            {areaIsOther && (
              <div className="input-group">
                <label className="input-label">Other Area</label>
                <input
                  className="input input-other"
                  value={selectedArea}
                  maxLength={100}
                  placeholder="e.g., Banjara Hills, Whitefield"
                  autoFocus
                  onChange={(event) => onAreaInput(event.target.value)}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {locationReady && isPincodeValid && !showMallForm && (
        <div className="mall-picker">
          <div className="mall-picker-header">
            <h5>Choose a mall</h5>
            <div className="mall-header-actions">
              {malls.length > 0 && (
                <span className="mall-count">{malls.length} found</span>
              )}
              <button
                type="button"
                className="mall-register-floating"
                onClick={() => {
                  setShowMallForm(true);
                  onCreateNew();
                }}
              >
                ＋ New mall
              </button>
            </div>
          </div>

          {isLoadingMalls && <div className="mall-loading">Loading malls...</div>}

          {!isLoadingMalls && (
            <>
              {selectedMallUuid && selectedMall && (
                <div className="mall-selected-card">
                  <div className="mall-selected-info">
                    <div className="mall-selected-name">{selectedMall.mallName}</div>
                    <div className="mall-selected-location">
                      <MapPin size={12} />
                      {selectedMall.area ? `${selectedMall.area}, ` : ''}{selectedMall.city || selectedCity}{selectedMall.state ? `, ${selectedMall.state}` : ''}{selectedMall.pincode ? ` - ${selectedMall.pincode}` : ''}
                    </div>
                  </div>
                  <button type="button" className="mall-clear-btn" onClick={onMallClear} title="Remove selection">
                    <X size={14} />
                  </button>
                </div>
              )}

              {!selectedMallUuid && (
                <>
                  {malls.length > 3 && (
                    <div className="input-group">
                      <label className="input-label">Search Malls</label>
                      <div className="mall-search-wrapper">
                        <Search className="mall-search-icon" size={14} />
                        <input
                          className="input mall-search-input"
                          value={mallSearch}
                          onChange={(event) => onMallSearch(event.target.value)}
                        />
                        {mallSearch && (
                          <button className="mall-search-clear" onClick={() => onMallSearch('')} type="button">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mall-card-list">
                    {malls.length === 0 && (
                      <div className="mall-empty-state">
                        <span className="mall-empty-icon"><Building2 size={24} /></span>
                        <span>No malls found in this area</span>
                      </div>
                    )}
                    {filteredMalls.map((mall) => (
                      <button
                        key={mall.mallUuid}
                        type="button"
                        className="mall-option-card"
                        onClick={() => onSelect(mall)}
                      >
                        <div className="mall-option-name">{mall.mallName}</div>
                        <div className="mall-option-meta">
                          <MapPin size={11} />
                          {mall.area ? `${mall.area}, ` : ''}{mall.city || ''}{mall.state ? `, ${mall.state}` : ''}{mall.pincode ? ` - ${mall.pincode}` : ''}
                        </div>
                      </button>
                    ))}
                    {zeroNameMatches && (
                      <div className="mall-empty-state">
                        <span>No malls found "{mallSearch}"</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {showMallForm && (
        <div className="mall-form">
          <div className="input-group">
            <label className="input-label">Mall Name</label>
            <input className="input" value={mallForm.mallName} maxLength={50} placeholder="e.g., City Center Mall, Express Avenue" onChange={(event) => onMallFormChange('mallName', event.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Description (Optional)</label>
            <input className="input" value={mallForm.description} maxLength={50} placeholder="e.g., Premium shopping destination" onChange={(event) => onMallFormChange('description', event.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Area / Colony (Optional)</label>
            <input className="input" value={mallForm.area} maxLength={100} placeholder="e.g., Sector 15, Golf Course Road" onChange={(event) => onMallFormChange('area', event.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Street / Landmark (Optional)</label>
            <input className="input" value={mallForm.street} maxLength={100} placeholder="e.g., Near Metro Station, Opposite Park" onChange={(event) => onMallFormChange('street', event.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Building Name (Optional)</label>
            <input className="input" value={mallForm.building} maxLength={100} placeholder="e.g., Tower A, Phase 2" onChange={(event) => onMallFormChange('building', event.target.value)} />
          </div>
          <div className="step-actions split">
            <button type="button" className="btn ghost" onClick={() => setShowMallForm(false)}>
              Cancel mall registration
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={onMallRegister}
              disabled={!locationReady || !mallForm.mallName}
              title={(!locationReady || !mallForm.mallName) ? 'Add a mall name to enable' : ''}
            >
              Register Mall
            </button>
          </div>
        </div>
      )}

      {!showMallForm && (
        <div className="step-actions">
          <button
            type="button"
            className="btn primary full"
            onClick={onContinue}
            disabled={!isPincodeValid || !selectedCity || !selectedArea}
            title={(!isPincodeValid || !selectedCity || !selectedArea) ? 'Add all required location fields to enable' : ''}
          >
            {selectedMallUuid ? 'Continue' : 'Continue without choosing a mall'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MallWizard;
